import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, Upload, Eye, EyeOff, Loader2, CircleCheckBig, Check, X, Eraser, Info, PlusCircle } from 'lucide-react';
import {
  formatCents,
  filterEligiblesView,
  ELIGIBILITY_DECISIONS,
  type EligiblesView,
  type EligiblesTabRow,
  type EligiblesFilters,
  type EligibilityDecision,
  type ProposalDecision,
  type ComputeTariffResult,
} from '@mdu-invoicing/shared';
import { useAuth } from '@/hooks/useAuth';
import { apiFetch, ApiError } from '@/lib/api-client';
import { currentMonth, monthBounds } from '@/lib/eligibles-query';
import { EligiblesFilterBar } from '@/components/eligibles/EligiblesFilterBar';
import { ExportModal } from '@/components/eligibles/ExportModal';
import { ImportModal, type ImportCommitInfo } from '@/components/eligibles/ImportModal';
import { RegisterModal, type RegisterSummary } from '@/components/eligibles/RegisterModal';
import { SubmissionBadge } from '@/components/eligibles/SubmissionBadge';
import { ProposalDecisionCell } from '@/components/eligibles/ProposalDecisionCell';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

type Stream = 'ssv' | 'tsa';

function decisionClasses(d: EligibilityDecision): string {
  if (d === 'OK') return 'border-emerald-300 bg-emerald-50 text-emerald-700';
  if (d === 'REMOVE') return 'border-rose-300 bg-rose-50 text-rose-700';
  return 'border-amber-300 bg-amber-50 text-amber-700';
}

/** Badge colours for SSV/TSA Validation Status (Validated / Refused / To validate). */
function validationClasses(status: string): string {
  const s = status.trim().toLowerCase();
  if (s === 'validated') return 'border-emerald-300 bg-emerald-50 text-emerald-700';
  if (s === 'refused') return 'border-rose-300 bg-rose-50 text-rose-700';
  return 'border-amber-300 bg-amber-50 text-amber-700'; // "To validate" / anything else
}

/** 'YYYY-MM-DD'(THH:MM…) → 'DD/MM/YYYY' for display; '—' when absent. */
function frDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

/**
 * The amount cell for a row's tariff. PRICED → the formatted euro amount; the two
 * non-priced cases are shown explicitly (never a fake €0): UNRESOLVED surfaces the
 * §4 gap (e.g. `ONLY BUDI` — pending Joël), OUT_OF_SCOPE a non-invoicing
 * contractor (or a NULL contractor on staff rows).
 */
function tariffDisplay(tariff: ComputeTariffResult): { text: string; muted: boolean } {
  if (tariff.status === 'PRICED') {
    // The amount is redacted to null server-side for a user without canViewAmounts
    // (the PRICED discriminant is intentionally kept). Never pass null to formatCents
    // — it throws a RangeError. The amount column is hidden in that case anyway
    // (pricesVisible), so this branch is just a safety net.
    const cents = (tariff as { amountCents: number | null }).amountCents;
    return cents == null ? { text: '•••', muted: true } : { text: formatCents(cents), muted: false };
  }
  if (tariff.status === 'UNRESOLVED') return { text: 'non tarifé', muted: true };
  return { text: 'hors périmètre', muted: true }; // OUT_OF_SCOPE
}

/** A persisted/edited decision for a grid row, kept client-side keyed `${stream}:${mduId}`. */
interface RowState {
  eligibility: EligibilityDecision;
  comment: string;
}

/** Staff Unifiber-proposal (Décision UF) state for a row, keyed the same way. */
interface ProposalState {
  decision: ProposalDecision | null;
  comment: string;
}

export function Eligibles() {
  const { isProvider, user, canViewAmounts } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Export/Import are modals on this hub screen. `?action=export|import` (from the
  // Dashboard quick actions) opens the matching modal on mount, then is stripped.
  const [exportOpen, setExportOpen] = useState(searchParams.get('action') === 'export');
  const [importOpen, setImportOpen] = useState(searchParams.get('action') === 'import');
  // Bumped after an import commits → re-runs the fetch effect to refresh the grid.
  const [reloadKey, setReloadKey] = useState(0);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [tab, setTab] = useState<Stream>('ssv');
  const [query, setQuery] = useState('');
  // Initial month: honour a ?from carried back from "Ajout d'un cas manqué" (so the provider
  // returns to the month they added for); otherwise the current month. The picker then drives it.
  const [month, setMonth] = useState<string>(() => {
    const from = searchParams.get('from');
    return from && /^\d{4}-\d{2}/.test(from) ? from.slice(0, 7) : currentMonth();
  });
  const [filters, setFilters] = useState<EligiblesFilters>({});
  const [showPrices, setShowPrices] = useState(false);
  // Amounts are server-gated: a user without canViewAmounts never receives them, and
  // the "Voir les prix" toggle is hidden. When allowed, the toggle stays a view
  // convenience (default off). pricesVisible drives every amount-bearing element.
  const pricesVisible = canViewAmounts && showPrices;

  const [view, setView] = useState<EligiblesView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-row decision state, keyed `${stream}:${mduId}`, seeded from the server
  // working-set overlay. `lastGood` mirrors the last server-confirmed value so a
  // failed save can be reverted. Only a PROVIDER persists edits.
  const [decisions, setDecisions] = useState<Record<string, RowState>>({});
  const lastGood = useRef<Record<string, RowState>>({});
  // Staff Unifiber-proposal (Décision UF) state, parallel to `decisions` and keyed the same.
  const [proposals, setProposals] = useState<Record<string, ProposalState>>({});
  const proposalLastGood = useRef<Record<string, ProposalState>>({});
  // The in-flight staff mass action (its target decision, or 'CLEAR'), or null when idle —
  // drives the per-button spinner and locks the grid while the bulk save runs.
  const [massBusy, setMassBusy] = useState<ProposalDecision | 'CLEAR' | null>(null);
  // Editing is allowed on ANY month, not just the current one (Jeremy 2026-07-08): SSV
  // addresses can be added/updated retroactively for an earlier period, so a "closed" past
  // month must NOT be read-only. The server already accepts any single month via ?from&to.
  const editable = isProvider;
  // STAFF review the Unifiber proposal (Décision UF / Commentaire décision) inline (Lot 3 — the former
  // Validation screen merged here).
  const staffEditable = !isProvider;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const { from, to } = monthBounds(month);
    apiFetch<EligiblesView>(`/eligibles?from=${from}&to=${to}`)
      .then((v) => {
        if (cancelled) return;
        setView(v);
        setError(null);
        // Seed decision + staff-proposal state from the working-set overlay carried on each row.
        const seed: Record<string, RowState> = {};
        const proposalSeed: Record<string, ProposalState> = {};
        const take = (rows: EligiblesTabRow[], stream: Stream) => {
          for (const r of rows) {
            if (r.decision) {
              const k = `${stream}:${r.source.mduId}`;
              seed[k] = { eligibility: r.decision.eligibility, comment: r.decision.comment };
              proposalSeed[k] = { decision: r.decision.proposalDecision, comment: r.decision.proposalComment };
            }
          }
        };
        take(v.ssv, 'ssv');
        take(v.tsa, 'tsa');
        setDecisions(seed);
        lastGood.current = seed;
        setProposals(proposalSeed);
        proposalLastGood.current = proposalSeed;
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  // Consume the ?action=export|import deep-link once (from the Dashboard quick
  // actions), then strip it so a refresh / modal-close doesn't reopen the modal.
  useEffect(() => {
    if (searchParams.has('action')) {
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Apply the table-B display filters (client-side, instant) over the fetched month
  // via the shared pure predicate. The filter-bar UI is the reusable EligiblesFilterBar.
  const filteredView = useMemo(() => (view ? filterEligiblesView(view, filters) : null), [view, filters]);

  const ssvRows = filteredView?.ssv ?? [];
  const tsaRows = filteredView?.tsa ?? [];
  const ssvCount = ssvRows.length;
  const tsaCount = tsaRows.length;

  const setFilter = (key: keyof EligiblesFilters, value: string) =>
    setFilters((f) => ({ ...f, [key]: value || undefined }));
  function resetFilters() {
    setFilters({});
    setQuery('');
  }
  function onMonthChange(v: string) {
    if (!v) return;
    setMonth(v);
    // A contractor/area picked for the old month may not exist in the new one — start clean.
    resetFilters();
  }

  /**
   * After an import commits: show EXACTLY what was just imported (Gerd 2026-08 — "the
   * import does not reset the current selection to display the file just imported").
   * Any leftover display filter could hide the imported rows, so clear them all, then
   * pin the "Fichier importé" filter to the new batch. `reloadKey` re-fetches the month
   * so the rows carry the new provenance the filter options are built from.
   */
  function onImportCommitted(info: ImportCommitInfo) {
    setQuery('');
    setFilters(info.importBatchId ? { importFile: info.importBatchId } : {});
    setReloadKey((k) => k + 1);
  }

  const rows = useMemo(() => {
    const base = tab === 'ssv' ? ssvRows : tsaRows;
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (r) =>
        r.source.mduId.toLowerCase().includes(q) ||
        (r.source.areaName ?? '').toLowerCase().includes(q) ||
        (r.source.areaId ?? '').toLowerCase().includes(q) ||
        (r.source.ssvContractor ?? '').toLowerCase().includes(q),
    );
  }, [tab, query, ssvRows, tsaRows]);

  // The stream is passed EXPLICITLY (captured at render = the visible tab), never read
  // from the live `tab` inside an async/blur callback — so a late save can never write
  // to the other tab (SSV/TSA are independent).
  const keyOf = (stream: Stream, mduId: string) => `${stream}:${mduId}`;
  function stateOf(r: EligiblesTabRow, stream: Stream): RowState {
    // Fall back to the row's own server overlay, then to the implicit OK default
    // (every returned synced row is eligible for its tab).
    return (
      decisions[keyOf(stream, r.source.mduId)] ?? {
        eligibility: r.decision?.eligibility ?? 'OK',
        comment: r.decision?.comment ?? '',
      }
    );
  }

  /** Persist one provider decision; OQ-6 (REMOVE/ADD need a comment) is enforced both
   *  here (skip the call until filled) and server-side. Reverts on failure unless a
   *  newer edit has superseded it. */
  async function persist(stream: Stream, r: EligiblesTabRow, next: RowState): Promise<boolean> {
    if (!editable) return true;
    const key = keyOf(stream, r.source.mduId);
    if ((next.eligibility === 'REMOVE' || next.eligibility === 'ADD') && next.comment.trim() === '') {
      return true; // wait for the (now-visible) comment field before saving (not a failure)
    }
    const prev = lastGood.current[key] ?? { eligibility: 'OK' as EligibilityDecision, comment: '' };
    if (prev.eligibility === next.eligibility && prev.comment === next.comment) return true; // no-op
    try {
      // Carry the SELECTED month so the write lands in the same invoicing month the
      // GET is showing — the PATCH route derives the month from ?from&to (falling back
      // to the CURRENT month otherwise), so omitting it would silently write a
      // non-current month's edit to the current month (wrong-month write + data loss).
      const { from, to } = monthBounds(month);
      await apiFetch(`/eligibles/decision?from=${from}&to=${to}`, {
        method: 'PATCH',
        body: JSON.stringify({
          stream: stream.toUpperCase(),
          mduId: r.source.mduId,
          eligibility: next.eligibility,
          comment: next.comment,
        }),
      });
      lastGood.current = { ...lastGood.current, [key]: next };
      return true;
    } catch (e) {
      // Revert the optimistic edit — but only if no newer edit superseded it (avoid
      // clobbering a later successful state when an older save fails out of order).
      setDecisions((d) => {
        const cur = d[key];
        return cur && cur.eligibility === next.eligibility && cur.comment === next.comment
          ? { ...d, [key]: prev }
          : d;
      });
      toast.error(e instanceof ApiError ? e.message : "Échec de l'enregistrement");
      return false;
    }
  }

  function onDecisionChange(stream: Stream, r: EligiblesTabRow, eligibility: EligibilityDecision) {
    const cur = stateOf(r, stream);
    const next = { eligibility, comment: cur.comment };
    setDecisions((d) => ({ ...d, [keyOf(stream, r.source.mduId)]: next }));
    void persist(stream, r, next); // OK saves now; REMOVE/ADD wait for a comment
  }
  function onCommentChange(stream: Stream, r: EligiblesTabRow, comment: string) {
    setDecisions((d) => ({
      ...d,
      [keyOf(stream, r.source.mduId)]: { eligibility: stateOf(r, stream).eligibility, comment },
    }));
  }

  // ── Staff Unifiber proposal (Décision UF) — merged from the former Validation screen ──────
  function proposalStateOf(r: EligiblesTabRow, stream: Stream): ProposalState {
    return (
      proposals[keyOf(stream, r.source.mduId)] ?? {
        decision: r.decision?.proposalDecision ?? null,
        comment: r.decision?.proposalComment ?? '',
      }
    );
  }

  /** Persist one staff proposal to `/validation/decision` (staff-only, keyed by contractor).
   *  Reverts the optimistic edit on failure unless superseded; `silent` mutes the per-row toast
   *  (used by the mass action). */
  async function persistProposal(
    stream: Stream,
    r: EligiblesTabRow,
    next: ProposalState,
    silent = false,
  ): Promise<boolean> {
    if (!staffEditable) return false;
    const contractor = r.source.ssvContractor;
    if (!contractor) {
      if (!silent) toast.error('Ligne hors périmètre (aucun contractant) — non révisable');
      return false;
    }
    const key = keyOf(stream, r.source.mduId);
    const prev = proposalLastGood.current[key] ?? { decision: null, comment: '' };
    if (prev.decision === next.decision && prev.comment === next.comment) return true; // no-op
    try {
      // Carry the SELECTED month so the write lands in the viewed month (the route derives the
      // month from ?from&to, else falls back to the current month).
      const { from, to } = monthBounds(month);
      await apiFetch(`/validation/decision?from=${from}&to=${to}`, {
        method: 'PATCH',
        body: JSON.stringify({
          stream: stream.toUpperCase(),
          mduId: r.source.mduId,
          contractor,
          proposalDecision: next.decision,
          proposalComment: next.comment,
        }),
      });
      proposalLastGood.current = { ...proposalLastGood.current, [key]: next };
      return true;
    } catch (e) {
      setProposals((p) => {
        const cur = p[key];
        return cur && cur.decision === next.decision && cur.comment === next.comment ? { ...p, [key]: prev } : p;
      });
      if (!silent) toast.error(e instanceof ApiError ? e.message : "Échec de l'enregistrement");
      return false;
    }
  }

  function setProposalDecision(stream: Stream, r: EligiblesTabRow, decision: ProposalDecision) {
    if (!staffEditable) return;
    const cur = proposalStateOf(r, stream);
    const next = { decision: cur.decision === decision ? null : decision, comment: cur.comment }; // toggle off
    setProposals((p) => ({ ...p, [keyOf(stream, r.source.mduId)]: next }));
    void persistProposal(stream, r, next);
  }
  function setProposalComment(stream: Stream, r: EligiblesTabRow, comment: string) {
    if (!staffEditable) return;
    setProposals((p) => ({
      ...p,
      [keyOf(stream, r.source.mduId)]: { decision: proposalStateOf(r, stream).decision, comment },
    }));
  }

  /** Mass action over the visible (filtered + searched) tab rows: set/clear every reviewable
   *  row's Décision UF, keeping each row's committed comment. Locks the grid while it runs. */
  async function massSet(decision: ProposalDecision | null) {
    if (!staffEditable || massBusy !== null) return;
    const targets = rows.filter((r) => r.source.ssvContractor);
    if (targets.length === 0) return;
    setMassBusy(decision ?? 'CLEAR');
    const commentOf = (r: EligiblesTabRow) =>
      proposalLastGood.current[keyOf(tab, r.source.mduId)]?.comment ?? r.decision?.proposalComment ?? '';
    setProposals((p) => {
      const nextMap = { ...p };
      for (const r of targets) nextMap[keyOf(tab, r.source.mduId)] = { decision, comment: commentOf(r) };
      return nextMap;
    });
    const results = await Promise.all(targets.map((r) => persistProposal(tab, r, { decision, comment: commentOf(r) }, true)));
    setMassBusy(null);
    const failed = results.filter((ok) => !ok).length;
    if (failed) toast.error(`${failed} décision(s) Unifiber non sauvegardée(s)`);
    else toast.success(decision === null ? 'Décisions Unifiber effacées' : 'Décisions Unifiber appliquées');
  }

  // Total of the OK rows that are actually priced (PRICED only — never count a
  // non-priced row as €0).
  const total = useMemo(
    () =>
      rows.reduce(
        (s, r) => (stateOf(r, tab).eligibility === 'OK' && r.tariff.status === 'PRICED' ? s + r.tariff.amountCents : s),
        0,
      ),
    [rows, tab, decisions],
  );

  // G2-13a — the month's decision breakdown for the "Valider et enregistrer" recap.
  // Counts over the FULL view (not the filtered subset): registering covers the month.
  const registerSummary = useMemo((): RegisterSummary => {
    const blank = () => ({ ok: 0, remove: 0, add: 0, incomplete: 0 });
    const tally = (rs: EligiblesTabRow[], stream: Stream, acc: ReturnType<typeof blank>) => {
      for (const r of rs) {
        const st = stateOf(r, stream);
        if (st.eligibility === 'OK') acc.ok += 1;
        else if (st.comment.trim() === '') acc.incomplete += 1; // REMOVE/ADD missing comment (OQ-6)
        else if (st.eligibility === 'REMOVE') acc.remove += 1;
        else acc.add += 1;
      }
    };
    const ssv = blank();
    const tsa = blank();
    if (view) {
      tally(view.ssv, 'ssv', ssv);
      tally(view.tsa, 'tsa', tsa);
    }
    const total = ssv.ok + ssv.remove + ssv.add + tsa.ok + tsa.remove + tsa.add;
    return { ssv, tsa, total, incomplete: ssv.incomplete + tsa.incomplete };
  }, [view, decisions]);

  // Flush every current decision to the working set (most are already saved on edit;
  // this also catches a just-typed comment), then confirm. persist() no-ops on an
  // unchanged row and skips an incomplete REMOVE/ADD (OQ-6), so the sweep is cheap.
  async function handleRegisterConfirm() {
    setRegistering(true);
    try {
      const all: [Stream, EligiblesTabRow][] = [
        ...(view?.ssv ?? []).map((r) => ['ssv', r] as [Stream, EligiblesTabRow]),
        ...(view?.tsa ?? []).map((r) => ['tsa', r] as [Stream, EligiblesTabRow]),
      ];
      const results = await Promise.all(all.map(([s, r]) => persist(s, r, stateOf(r, s))));
      // Don't record a "submitted" milestone if a decision failed to save — the draft
      // would be stale-but-submitted. The per-row toast already flagged the failure.
      if (results.includes(false)) {
        toast.error("Certaines décisions n'ont pas pu être enregistrées — réessayez avant de soumettre.");
        return; // keep the modal open (finally clears the spinner) for a retry
      }
      // G2-13b: record the explicit submission milestone for the viewed month, so staff
      // see "Soumis" (and any later decision edit shows "modifié depuis"). Runs AFTER the
      // flush, so submittedAt > every just-saved decisionUpdatedAt (no false "modifié").
      const { from, to } = monthBounds(month);
      await apiFetch(`/eligibles/submit?from=${from}&to=${to}`, { method: 'POST' });
      toast.success(`Validation soumise — ${registerSummary.total} décision(s)`);
      setRegisterOpen(false);
      setReloadKey((k) => k + 1); // refresh → the "Soumis" badges appear
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec de la soumission');
    } finally {
      setRegistering(false);
    }
  }

  // Staff mass-action enablement: reviewable = visible rows with a contractor (a NULL-contractor
  // staff row can't be proposal-decided). massBusyOrReadonly locks the grid during a bulk save.
  const reviewableCount = rows.filter((r) => r.source.ssvContractor).length;
  const massBusyOrReadonly = !staffEditable || massBusy !== null;

  // Base columns (both tabs, all roles): MDU_ID, DMP, Address Type, Area Name, Status,
  // Done, Upload Date, Validation, Éligibilité, Commentaire éligibilité = 10. +Quadrant (SSV).
  // +3 staff-only (Contractant, Décision UF, Commentaire décision). +Montant (prices visible).
  // Keep in sync with thead/cells.
  const colCount = 10 + (tab === 'ssv' ? 1 : 0) + (isProvider ? 0 : 3) + (pricesVisible ? 1 : 0);

  return (
    <div className="space-y-5">
      {/* Tabs + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
          {(['ssv', 'tsa'] as Stream[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              disabled={massBusy !== null}
              className={cn(
                'flex items-center gap-2 rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                tab === t ? 'bg-brand-gradient text-white shadow-sm' : 'text-slate-500 hover:text-slate-800',
              )}
            >
              {t.toUpperCase()}
              <span
                className={cn(
                  'rounded-full px-1.5 text-xs font-bold',
                  tab === t ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-500',
                )}
              >
                {t === 'ssv' ? ssvCount : tsaCount}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {isProvider && (
            <Link
              to={`/add?from=${monthBounds(month).from}&to=${monthBounds(month).to}`}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              <PlusCircle className="h-4 w-4 text-brand-purple" />
              Cas manqué
            </Link>
          )}
          {canViewAmounts && (
            <button
              type="button"
              onClick={() => setShowPrices((v) => !v)}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              {showPrices ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {showPrices ? 'Masquer les prix' : 'Voir les prix'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            <Upload className="h-4 w-4 text-brand-purple" />
            Importer
          </button>
          <button
            type="button"
            onClick={() => setExportOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-brand-gradient px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
          >
            <Download className="h-4 w-4" />
            Exporter
          </button>
          {editable && (
            <button
              type="button"
              onClick={() => setRegisterOpen(true)}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700"
            >
              <CircleCheckBig className="h-4 w-4" />
              Valider et enregistrer
            </button>
          )}
        </div>
      </div>

      {/* Filter bar (spec table B) — reusable component; shows the active tab's stream filters.
          The imported-file filter is shown to BOTH roles (Jeremy 2026-08-12: a partner must be
          able to review the file it just imported); the submission filter stays staff-only. */}
      <EligiblesFilterBar
        view={view}
        filters={filters}
        onChange={setFilter}
        onReset={resetFilters}
        month={month}
        onMonthChange={onMonthChange}
        isProvider={isProvider}
        streams={[tab]}
        query={query}
        onQueryChange={setQuery}
        showSubmissionFilter={!isProvider}
        showImportFileFilter
      />

      {/* STAFF: what the Décision UF columns mean (carried over from the former Validation screen). */}
      {!isProvider && (
        <div className="flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-2.5 text-xs text-sky-800">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <p>
            Revue Unifiber : l'<span className="font-semibold">éligibilité</span> (OK / REMOVE / ADD) est la
            décision du partenaire (lecture seule ici). Le <span className="font-semibold">Statut validation (PBI)</span>{' '}
            est le statut issu de Power&nbsp;BI (source, lecture seule) — à ne pas confondre avec votre{' '}
            <span className="font-semibold">Décision UF</span> (Accepté / Refusé), la proposition de l'app enregistrée
            immédiatement ; une ligne <span className="font-semibold">Refusé</span> est exclue de l'étape
            « Documenter » <span className="opacity-70">(règle à confirmer avec Gerd)</span>.
          </p>
        </div>
      )}

      {/* STAFF: Décision Unifiber mass actions over the visible filter (Lot 3 — merged Validation). */}
      {!isProvider && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-400">
            Décision Unifiber — tout le filtre ({tab.toUpperCase()}) :
          </span>
          <button
            type="button"
            disabled={massBusyOrReadonly || reviewableCount === 0}
            onClick={() => void massSet('ACCEPTED')}
            className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {massBusy === 'ACCEPTED' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Tout accepter
          </button>
          <button
            type="button"
            disabled={massBusyOrReadonly || reviewableCount === 0}
            onClick={() => void massSet('REFUSED')}
            className="flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {massBusy === 'REFUSED' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
            Tout refuser
          </button>
          <button
            type="button"
            disabled={massBusyOrReadonly || reviewableCount === 0}
            onClick={() => void massSet(null)}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {massBusy === 'CLEAR' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eraser className="h-3.5 w-3.5" />}
            Effacer
          </button>
        </div>
      )}

      {loading ? (
        <Card className="flex items-center gap-2 p-5 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement des éligibles…
        </Card>
      ) : error ? (
        <Card className="p-5 text-sm text-rose-700">Impossible de charger les éligibles : {error}.</Card>
      ) : (
        /* Grid */
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full min-w-[1200px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {/* Frozen first column (Gerd 2026-08): stays put on horizontal scroll.
                      Needs its OWN opaque background — a sticky cell paints over the
                      scrolled content, so the <tr> background alone would let it show
                      through. z-20 keeps the header above the sticky body cells. */}
                  <th className="sticky left-0 z-20 border-r border-slate-200 bg-slate-50 px-4 py-3">MDU_ID</th>
                  <th className="px-4 py-3">DMP</th>
                  <th className="px-4 py-3">Type d'adresse</th>
                  <th className="px-4 py-3">Area Name</th>
                  {tab === 'ssv' && <th className="px-4 py-3">Quadrant</th>}
                  <th className="px-4 py-3">{tab === 'ssv' ? 'SSV Status' : 'TSA Status'}</th>
                  <th className="px-4 py-3">{tab === 'ssv' ? 'SSV Done' : 'TSA Done'}</th>
                  <th className="px-4 py-3">Date d'upload</th>
                  {/* PBI-source validation status (read-only) — labelled "(PBI)" to distinguish
                      it from the app's own "Décision UF" proposal column (Lot 5, Gerd). */}
                  <th className="px-4 py-3">Statut validation (PBI)</th>
                  {!isProvider && <th className="px-4 py-3">Contractant</th>}
                  <th className="px-4 py-3">Éligibilité</th>
                  {/* Gerd 2026-08: spell out WHICH comment each column holds. */}
                  <th className="px-4 py-3">Commentaire éligibilité</th>
                  {!isProvider && <th className="px-4 py-3">Décision UF</th>}
                  {!isProvider && <th className="px-4 py-3">Commentaire décision</th>}
                  {pricesVisible && <th className="px-4 py-3 text-right">Montant</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const st = stateOf(r, tab);
                  const d = st.eligibility;
                  const ps = proposalStateOf(r, tab);
                  const amount = tariffDisplay(r.tariff);
                  const statusValue = tab === 'ssv' ? r.source.ssvDone : r.source.tsaDone;
                  const dateValue = tab === 'ssv' ? r.source.ssvDate : r.source.tsaDate;
                  const validationStatus =
                    tab === 'ssv' ? r.source.ssvValidationStatus : r.source.tsaValidationStatus;
                  const isRed = r.decision?.dmpFlag === 'RED';
                  return (
                    // `group` + an OPAQUE hover (not /60) so the frozen first cell can
                    // mirror the row hover exactly — a translucent sticky background
                    // would let the scrolled-under cells show through.
                    <tr key={r.source.mduId} className="group hover:bg-slate-50">
                      <td className="sticky left-0 z-10 whitespace-nowrap border-r border-slate-200 bg-white px-4 py-2.5 font-mono text-xs text-slate-700 group-hover:bg-slate-50">
                        <div className="flex flex-col gap-1">
                          <span>{r.source.mduId}</span>
                          {r.alreadyInvoiced &&
                            (isProvider ? (
                              <span
                                className="inline-flex w-fit items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-600/20"
                                title="Déjà présent au journal de facturation"
                              >
                                uploaded
                              </span>
                            ) : (
                              <Link
                                to={`/log?mduId=${encodeURIComponent(r.source.mduId)}`}
                                className="inline-flex w-fit items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-600/20 transition-colors hover:bg-amber-100"
                                title="Déjà présent au journal de facturation — ouvrir le journal"
                              >
                                uploaded →
                              </Link>
                            ))}
                          <SubmissionBadge submission={r.submission} />
                        </div>
                      </td>
                      {/* MDU_in_DMP flag — RED for an import/manual-added row not (yet) in vwMDU */}
                      <td className="px-4 py-2.5">
                        {isRed ? (
                          <Badge variant="red" dot>
                            DMP
                          </Badge>
                        ) : (
                          <Badge variant="green" dot>
                            DMP
                          </Badge>
                        )}
                      </td>
                      {/* Building Group Address Type — always 'Main Address' for eligible rows (constraint) */}
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                        {r.source.buildingGroupAddressType ?? '—'}
                      </td>
                      {/* Area Name — joined from vwArea (G2-7b); falls back to areaId if unmatched */}
                      <td className="px-4 py-2.5 font-semibold text-slate-800">
                        {r.source.areaName ?? r.source.areaId ?? '—'}
                      </td>
                      {tab === 'ssv' && (
                        <td className="px-4 py-2.5">
                          <Badge variant="purple">{r.source.designQuadrant ?? '—'}</Badge>
                        </td>
                      )}
                      {/* SSV/TSA Status (distinct from the Done field below) */}
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                        {(tab === 'ssv' ? r.source.ssvStatus : r.source.tsaStatus) ?? '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{statusValue ?? '—'}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{frDate(dateValue)}</td>
                      {/* SSV/TSA Validation Status — Validated / Refused / To validate */}
                      <td className="px-4 py-2.5">
                        {validationStatus ? (
                          <span
                            className={cn(
                              'inline-flex w-fit rounded-md border px-2 py-0.5 text-xs font-semibold',
                              validationClasses(validationStatus),
                            )}
                          >
                            {validationStatus}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      {!isProvider && (
                        <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">{r.source.ssvContractor ?? '—'}</td>
                      )}
                      {/* Éligibilité — OK/REMOVE/ADD (provider-editable on the current month) */}
                      <td className="px-4 py-2.5">
                        {editable ? (
                          <select
                            value={d}
                            aria-label={`Éligibilité ${r.source.mduId}`}
                            onChange={(e) => onDecisionChange(tab, r, e.target.value as EligibilityDecision)}
                            className={cn(
                              'rounded-md border px-2 py-1 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand-purple/20',
                              decisionClasses(d),
                            )}
                          >
                            {ELIGIBILITY_DECISIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className={cn(
                              'inline-flex w-fit rounded-md border px-2 py-1 text-xs font-semibold',
                              decisionClasses(d),
                            )}
                          >
                            {d}
                          </span>
                        )}
                      </td>
                      {/* Commentaire d'éligibilité — dedicated column, always visible; provider-editable
                          on the current month. OQ-6: REMOVE/ADD require a comment (amber hint here +
                          enforced in persist() and the register guard). */}
                      <td className="px-4 py-2.5">
                        {editable ? (
                          <input
                            value={st.comment}
                            onChange={(e) => onCommentChange(tab, r, e.target.value)}
                            onBlur={() => void persist(tab, r, stateOf(r, tab))}
                            aria-label={`Commentaire éligibilité ${r.source.mduId}`}
                            placeholder={d === 'OK' ? 'Commentaire (optionnel)' : 'Commentaire (obligatoire)'}
                            className={cn(
                              'w-48 rounded-md border px-2 py-1 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-purple/20',
                              d !== 'OK' && st.comment.trim() === ''
                                ? 'border-amber-300 focus:border-amber-400'
                                : 'border-slate-200 focus:border-brand-purple',
                            )}
                          />
                        ) : st.comment ? (
                          <span className="text-xs text-slate-600">{st.comment}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      {/* Décision UF + Commentaire décision — Unifiber proposal, staff-editable
                          (Lot 3: the former Validation screen merged here). */}
                      {!isProvider && (
                        <ProposalDecisionCell
                          decision={ps.decision}
                          comment={ps.comment}
                          editable={staffEditable}
                          disabled={massBusy !== null || !r.source.ssvContractor}
                          mduId={r.source.mduId}
                          onDecision={(dec) => setProposalDecision(tab, r, dec)}
                          onComment={(c) => setProposalComment(tab, r, c)}
                          onCommentBlur={() => void persistProposal(tab, r, proposalStateOf(r, tab))}
                        />
                      )}
                      {pricesVisible && (
                        <td
                          className={cn(
                            'whitespace-nowrap px-4 py-2.5 text-right font-semibold tabular-nums',
                            amount.muted ? 'text-slate-400' : 'text-slate-800',
                          )}
                        >
                          {amount.text}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={colCount} className="px-4 py-12 text-center text-sm text-slate-400">
                      Aucun enregistrement éligible pour ce filtre.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer totals */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm">
            <span className="text-slate-500">
              <span className="font-bold text-slate-800">{rows.length}</span> enregistrement(s) ·{' '}
              <span className="font-semibold text-slate-700">{tab.toUpperCase()}</span>
              {isProvider && user?.contractorName && (
                <>
                  {' '}
                  · périmètre <span className="font-semibold text-brand-purple">{user.contractorName}</span>
                </>
              )}
            </span>
            {pricesVisible && (
              <span className="text-slate-500">
                Total éligibles (OK, tarifés) ·{' '}
                <span className="font-bold tabular-nums text-slate-900">{formatCents(total)}</span>
              </span>
            )}
          </div>
        </div>
      )}

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        month={month}
        filters={filters}
        view={view}
      />
      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        month={month}
        onCommitted={onImportCommitted}
      />
      {editable && (
        <RegisterModal
          open={registerOpen}
          onClose={() => setRegisterOpen(false)}
          summary={registerSummary}
          onConfirm={() => void handleRegisterConfirm()}
          registering={registering}
        />
      )}
    </div>
  );
}
